import { useMutation, useQueryClient } from 'react-query';

import { Controller, useForm } from 'react-hook-form';

import * as yup from 'yup';

import { State } from '@hookstate/core';
import { yupResolver } from '@hookform/resolvers/yup';

import CloseImage from '@assets/svg/close-btn.svg';

import { CustomPicker } from '@components/CustomPickerWithBorder/CustomPicker';

import EquipmentStatus, {
  EquipmentStatusCategoryOptions,
} from '@models/EquipmentStatus.model';

import {
  deleteEquipmentStatus,
  deleteInUseEquipmentStatus,
} from '@services/queries/equipmentStatus';
import { IVerifyInUseEquipmentStatusResponse } from '@services/queries/equipmentStatus/verifyInUseEquipmentStatus/verifyInUseEquipmentStatus';

import { StatusBadge } from '@components';

import { useContext } from 'react';
import { NavigationContext } from '@layout/navigation/navigation';
import { updateDefaultStatus } from '@services/queries/studios/updateDefaultStatus/updateDefaultStatus';
import {
  Close,
  Header,
  Title,
  Confirm,
  Label,
  Line,
  Form,
  Divider,
  Text,
  Cencel,
  Info,
  Optional,
  LineDivider,
} from './DeleteEquipmentStatus.styles';

interface IDeleteEquipmentStatusRaw {
  open: State<boolean>;
  equipmentStatus: EquipmentStatus;
  equipmentStatuses: EquipmentStatus[];
  verifyStatus: IVerifyInUseEquipmentStatusResponse;
  defaultStatus: string | undefined;
}

interface IFormData {
  type: EquipmentStatusCategoryOptions;
  newDefaultStatus: string;
}

export const DeleteEquipmentStatusRaw = ({
  open,
  equipmentStatus,
  equipmentStatuses,
  verifyStatus,
  defaultStatus,
}: IDeleteEquipmentStatusRaw) => {
  const options = equipmentStatuses.map(es => ({
    value: es.id,
    label: <StatusBadge label={es.category} name={es.name} />,
  }));
  const { studioId } = useContext(NavigationContext);

  const queryClient = useQueryClient();

  const schema = yup
    .object({
      type: yup.string(),
      newDefaultStatus: yup.string(),
    })
    .required();

  const {
    handleSubmit,
    formState: { isValid },
    control,
    reset,
    clearErrors,
    watch,
  } = useForm<IFormData>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const close = () => {
    open.set(false);
    reset();
    clearErrors('type');
  };

  const patchDefaultStatusMutation = useMutation(updateDefaultStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries(['defaultStatus', studioId]);
    },
  });

  const deleteEquipmentStatusMutation = useMutation(deleteEquipmentStatus, {
    onSuccess: () => {
      queryClient.invalidateQueries(['studios', studioId, 'customStatus']);
      close();
    },
  });

  const deleteInUseEquipmentStatusMutation = useMutation(
    deleteInUseEquipmentStatus,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['studios', studioId, 'customStatus']);
        close();
      },
    },
  );

  const onSubmit = (formData: IFormData) => {
    patchDefaultStatusMutation.mutate({
      id: studioId,
      equipmentStatusId: formData.newDefaultStatus,
    });
    deleteInUseEquipmentStatusMutation.mutate({
      id: equipmentStatus.id,
      newEquipmentStatusId: formData.type,
    });
  };

  const submitNotInUseWithDefaultStatus = (formData: IFormData) => {
    patchDefaultStatusMutation.mutate({
      id: studioId,
      equipmentStatusId: formData.newDefaultStatus,
    });
    deleteEquipmentStatusMutation.mutate(equipmentStatus.id);
  };

  const submitNotInUse = () => {
    deleteEquipmentStatusMutation.mutate(equipmentStatus.id);
  };

  if (verifyStatus.onlyLeft) {
    return (
      <div>
        <Header>
          <Title>Status cannot be deleted</Title>
          <Close onClick={close}>
            <img src={CloseImage} alt="close" />
          </Close>
        </Header>
        <Text>
          When deleting a status you must select what status will replace the
          one being deleted, and there are no other status for this Studio.
        </Text>
        <Text>Please add a new Status before deleting this.</Text>
        <Line>
          <Cencel type="button" onClick={close}>
            Cancel
          </Cencel>
        </Line>
      </div>
    );
  }

  if (!verifyStatus.inUse && defaultStatus === equipmentStatus.id) {
    return (
      <div>
        <Header>
          <Title>Default Status</Title>
          <Close onClick={close}>
            <img src={CloseImage} alt="close" />
          </Close>
        </Header>
        <Text>
          This status is set as default. <br />
          Choose a replacement if applicable
        </Text>

        <Optional>New Default Status (Optional)</Optional>

        <Controller
          control={control}
          name="newDefaultStatus"
          render={({ field: { onChange, value } }) => (
            <CustomPicker
              options={options}
              value={options.find(c => c.value === value)}
              onChange={(val: any) => {
                onChange(val.value);
              }}
              placeholder="Select"
            />
          )}
        />

        <Line>
          <Confirm
            type="button"
            onClick={handleSubmit(submitNotInUseWithDefaultStatus)}
          >
            Delete
          </Confirm>
        </Line>
      </div>
    );
  }

  if (!verifyStatus.inUse) {
    return (
      <div>
        <Header>
          <Title>Are you sure?</Title>
          <Close onClick={close}>
            <img src={CloseImage} alt="close" />
          </Close>
        </Header>
        <Text>This action cannot be undone.</Text>
        <Line>
          <Confirm type="button" onClick={handleSubmit(submitNotInUse)}>
            Delete
          </Confirm>
        </Line>
      </div>
    );
  }

  return (
    <div>
      <Header>
        <Title>Are you sure?</Title>
        <Close onClick={close}>
          <img src={CloseImage} alt="close" />
        </Close>
      </Header>
      <Text>
        There are {verifyStatus.assets} assets with this status. This action
        causes loss of data and cannot be undone.
      </Text>
      <Divider />
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Label>Replacement Status</Label>

        <Controller
          control={control}
          name="type"
          render={({ field: { onChange, value } }) => (
            <CustomPicker
              options={options}
              value={options.find(c => c.value === value)}
              onChange={(val: any) => {
                onChange(val.value);
              }}
              placeholder="Select"
            />
          )}
        />
        <Info>
          When deleting a status you must select what status will replace the
          one being deleted
        </Info>

        {defaultStatus === equipmentStatus.id ? (
          <div>
            <LineDivider />

            <Text>
              This status is set as default. <br />
              Choose a replacement if applicable
            </Text>

            <Optional>New Default Status (Optional)</Optional>

            <Controller
              control={control}
              name="newDefaultStatus"
              render={({ field: { onChange, value } }) => (
                <CustomPicker
                  options={options}
                  value={options.find(c => c.value === value)}
                  onChange={(val: any) => {
                    onChange(val.value);
                  }}
                  placeholder="Select"
                />
              )}
            />
          </div>
        ) : (
          <div />
        )}

        <Line>
          <Confirm disabled={!isValid || !watch('type')} type="submit">
            Delete
          </Confirm>
        </Line>
      </Form>
    </div>
  );
};
